import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, AlertCircle } from "lucide-react";
import { api } from "@/api/client";
import CompassHeader from "@/components/dashboard/CompassHeader";
import LandasanHukumPanel from "@/components/dashboard/LandasanHukumPanel";
import StakeholderMapPanel from "@/components/dashboard/StakeholderMapPanel";
import ProsesBisnisPanel from "@/components/dashboard/ProsesBisnisPanel";
import KinerjaPanel from "@/components/dashboard/KinerjaPanel";
import CompassDocumentTable from "@/components/dashboard/CompassDocumentTable";
import DocumentDetailModal from "@/components/dashboard/DocumentDetailModal";

// ── Types matching CompassResponse ───────────────────────────────────────────

interface JenisRef {
  id: string;
  kode: string;
  nama: string;
}

interface LandasanHukumItem {
  id: string;
  judul: string;
  nomor_dokumen?: string;
  bentuk_dokumen_kode: string;
  bentuk_dokumen_nama: string;
  status_kode: string;
  status_nama: string;
  tanggal_berlaku?: string;
  tanggal_berakhir?: string;
  is_active: boolean;
}

interface SiklusProgress {
  kode: string;
  nama: string;
  uploaded_count: number;
  completion_pct: number;
  status_color: string;
}

interface SOPItem {
  id: string;
  judul: string;
  nomor_dokumen?: string;
  status_kode: string;
  status_nama: string;
}

interface CountByCategory {
  kode: string;
  nama: string;
  count: number;
}

interface ExpiryAlert {
  document_id: string;
  judul: string;
  jenis_pnbp_nama: string;
  tanggal_berakhir: string;
  days_remaining: number;
  tingkat_kerahasiaan: string;
}

interface TrendPoint {
  period: string;
  count: number;
  label: string;
}

interface TrendData {
  monthly_uploads: TrendPoint[];
  monthly_approvals: TrendPoint[];
}

interface KnowledgeItem {
  id: string;
  judul: string;
  total_views: number;
  kategori: string;
}

interface StakeholderInfo {
  pic_nama?: string;
  pic_jabatan?: string;
  unit_kerja?: string;
  stakeholder_eksternal: string[];
  hierarki: string[];
}

interface CompassResponse {
  jenis_pnbp?: JenisRef;
  tahun: number;
  landasan_hukum: LandasanHukumItem[];
  stakeholder: StakeholderInfo;
  siklus_progress: SiklusProgress[];
  sop_list: SOPItem[];
  total_documents: number;
  approved_count: number;
  pending_count: number;
  approved_pct: number;
  by_status: CountByCategory[];
  expiring_soon: number;
  expiry_alerts: ExpiryAlert[];
  trend: TrendData;
  knowledge_items: KnowledgeItem[];
}

// ── Page Component ───────────────────────────────────────────────────────────

export default function DashboardExecutivePage() {
  const currentYear = new Date().getFullYear();
  const [selectedJenisId, setSelectedJenisId] = useState("");
  const [selectedTahun, setSelectedTahun] = useState(currentYear);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableContext, setTableContext] = useState<{
    title: string;
    siklusFilter?: string;
    statusFilter?: string;
  } | null>(null);
  const [detailDocId, setDetailDocId] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<CompassResponse>({
    queryKey: ["dashboard", "compass", selectedJenisId, selectedTahun],
    queryFn: () =>
      api.get("/dashboard/compass", {
        ...(selectedJenisId ? { jenis_pnbp_id: selectedJenisId } : {}),
        tahun: selectedTahun,
      }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        <span className="ml-2 text-sm text-gray-500">Memuat dashboard executive...</span>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
        <AlertCircle className="h-5 w-5 flex-shrink-0" />
        <span className="text-sm">Gagal memuat dashboard executive.</span>
      </div>
    );
  }

  const normalizedSearch = searchQuery.toLowerCase().trim();

  return (
    <div className="space-y-5">
      {/* Header with filters */}
      <CompassHeader
        selectedJenisId={selectedJenisId}
        selectedTahun={selectedTahun}
        searchQuery={searchQuery}
        onJenisChange={setSelectedJenisId}
        onTahunChange={setSelectedTahun}
        onSearchChange={setSearchQuery}
        compassData={data}
      />

      {/* ═══ 3-Column Layout ═══ */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3" style={{ minHeight: "600px" }}>

        {/* ── Kolom Kiri: Landasan Hukum + Stakeholder ── */}
        <div className="space-y-5 flex flex-col">
          <div className="flex-1">
            <LandasanHukumPanel
              items={data.landasan_hukum}
              searchQuery={normalizedSearch}
              onViewAll={() =>
                setTableContext({ title: "Dokumen Landasan Hukum (Regulasi)" })
              }
            />
          </div>
          <StakeholderMapPanel data={data.stakeholder} />
        </div>

        {/* ── Kolom Tengah: Proses Bisnis ── */}
        <ProsesBisnisPanel
          items={data.siklus_progress}
          sopList={data.sop_list}
          onSiklusClick={(kode) => {
            const siklus = data.siklus_progress.find((s) => s.kode === kode);
            setTableContext({
              title: `Dokumen Siklus: ${siklus?.nama || kode}`,
              siklusFilter: kode,
            });
          }}
        />

        {/* ── Kolom Kanan: Dashboard Kinerja ── */}
        <KinerjaPanel
          totalDocuments={data.total_documents}
          approvedCount={data.approved_count}
          pendingCount={data.pending_count}
          approvedPct={data.approved_pct}
          byStatus={data.by_status}
          expiringSoon={data.expiring_soon}
          expiryAlerts={data.expiry_alerts}
          knowledgeItems={data.knowledge_items}
          tahun={data.tahun}
          onStatusClick={(kode, nama) =>
            setTableContext({ title: `Dokumen Status: ${nama}`, statusFilter: kode })
          }
          onExpiryClick={() =>
            setTableContext({ title: "Dokumen Segera Kedaluwarsa (30 Hari)" })
          }
          onViewAllDocs={() =>
            setTableContext({ title: "Semua Dokumen" })
          }
        />
      </div>

      {/* ═══ Tabel Rinci Dokumen (muncul saat klik widget) ═══ */}
      {tableContext && (
        <CompassDocumentTable
          title={tableContext.title}
          jenisId={selectedJenisId || undefined}
          siklusFilter={tableContext.siklusFilter}
          initialStatusFilter={tableContext.statusFilter}
          onClose={() => setTableContext(null)}
          onDocClick={(id) => setDetailDocId(id)}
        />
      )}

      {/* ═══ Document Detail Modal ═══ */}
      {detailDocId && (
        <DocumentDetailModal
          documentId={detailDocId}
          onClose={() => setDetailDocId(null)}
        />
      )}
    </div>
  );
}
